import { useState, useEffect } from 'react';
import LandingPage from '../components/LandingPage';
import { detectLocale } from '../locales/config';

const GERMAN_LOCALES = ['de-de', 'de-at', 'de-ch', 'de-li'];

const faqItems = [
  {
    q: "Wie funktioniert der Schuldenrechner?",
    a: "Gib jede Schuld mit dem aktuellen Saldo, dem Jahreszinssatz und der monatlichen Mindestzahlung ein. Der Schuldenrechner wendet die Schneeball- oder Lawinenmethode an und zeigt dir genau, wann du schuldenfrei bist, wie viel Zinsen du insgesamt zahlst und wie viel du sparst, wenn du jeden Monat etwas mehr zahlst.",
  },
  {
    q: "Was ist besser: Schneeball- oder Lawinenmethode?",
    a: "Das hängt von deinem Profil ab. Die Lawinenmethode (zuerst die Schuld mit dem höchsten Zinssatz tilgen) spart insgesamt mehr Zinsen. Die Schneeball-Methode (zuerst die kleinste Schuld tilgen) bringt schnelle Erfolge und hält die Motivation hoch. Beide sind deutlich besser als nur die Mindestzahlung zu leisten.",
  },
  {
    q: "Wie lange dauert es, meine Schulden abzuzahlen?",
    a: "Das hängt von deinem Saldo, deinem Zinssatz und deiner monatlichen Zahlung ab. Bei nur der Mindestzahlung auf eine Kreditkarte kann es über 10 Jahre dauern, schuldenfrei zu werden. Mit einem monatlichen Zusatzbetrag kannst du es in 2–3 Jahren schaffen und Tausende an Zinsen sparen. Nutze unseren Schuldenfrei-Rechner für deinen persönlichen Schuldenplan.",
  },
  {
    q: "Ist dieser Rechner kostenlos?",
    a: "Der Schuldenrechner ist vollständig kostenlos und erfordert keine Anmeldung. Du kannst deinen Schuldenplan in Sekunden erstellen. Wenn du deinen persönlichen Plan als PDF speichern und herunterladen möchtest, gibt es eine Einmalzahlung ohne Abonnement.",
  },
  {
    q: "Funktioniert er für Kreditkarten und Kredite?",
    a: "Ja. Dieser Kredit-abzahlen-Rechner funktioniert für Kreditkarten, Ratenkredite, Autokredite, Studienkredite und jede Art von verzinslicher Schuld. Du kannst mehrere Schulden gleichzeitig eingeben, um die Gesamtzeit zu berechnen, bis du schuldenfrei bist.",
  },
];

const COLORS = { text: "#e8e8f0", muted: "#9999b5", accent: "#00e5a0" };

const sections = [
  {
    heading: 'So nutzt du diesen Schuldenplan-Rechner',
    body: 'Gib jede Schuld ein: aktueller Saldo, Jahreszinssatz und monatliche Mindestzahlung. Füge jeden zusätzlichen Betrag hinzu, den du für die Tilgung aufbringen kannst. Der Schuldenrechner zeigt dir dein Schuldenfreiheitsdatum, die Gesamtzinsen und wie jede Strategie — Schneeball oder Lawine — deinen Weg zum Schuldenfrei-Werden beschleunigt.',
  },
  {
    heading: 'Schneeball- vs. Lawinenmethode: Was ist besser?',
    body: (
      <>
        <p style={{ color: COLORS.muted, lineHeight: 1.7, marginBottom: 12 }}>
          Die <strong style={{ color: COLORS.text }}>Schneeball-Methode</strong> zahlt zuerst die kleinste
          Schuld ab und überträgt die freigewordene Rate auf die nächste. Sie ist ideal,
          wenn du schnelle Erfolge brauchst, um motiviert zu bleiben und Schulden schneller abzuzahlen.
        </p>
        <p style={{ color: COLORS.muted, lineHeight: 1.7, marginBottom: 0 }}>
          Die <strong style={{ color: COLORS.text }}>Lawinenmethode</strong> tilgt zuerst die Schuld mit
          dem höchsten Zinssatz. Sie ist mathematisch effizienter und spart insgesamt mehr Geld.
          Nutze unseren Schuldenfrei-Rechner, um zu berechnen, wie viel du mit jeder Methode sparst
          und wie du Schulden schneller abzahlen kannst.
        </p>
      </>
    ),
  },
  {
    heading: 'Warum Mindestzahlungen dich Jahre kosten',
    body: 'Kreditkartenzinsen fallen monatlich auf den verbleibenden Saldo an. Wer nur die Mindestzahlung leistet, kann über 8–10 Jahre brauchen, um schuldenfrei zu werden. Schon 10–15 % mehr als die Mindestzahlung kann diese Zeit halbieren und Tausende an Zinsen sparen. Nutze den Kredit-abzahlen-Rechner, um den genauen Einfluss jedes zusätzlichen Euros zu sehen.',
  },
];

export default function Schuldenrechner() {
  const [locale, setLocale] = useState('de-de');

  useEffect(() => {
    detectLocale().then(detected => {
      setLocale(GERMAN_LOCALES.includes(detected) ? detected : 'de-de');
    });
  }, []);

  return (
    <LandingPage
      locale={locale}
      pageUrl="https://clearmydebt.app/schuldenrechner/"
      helmet={{
        lang: 'de',
        title: 'Schuldenrechner Kostenlos — Schuldenfrei Werden | ClearMyDebt',
        description: 'Berechne wann du schuldenfrei bist. Schneeball- und Lawinenmethode. Ohne Anmeldung. Sofortige Ergebnisse.',
        ogTitle: 'Schuldenrechner Kostenlos — Schuldenfrei Werden | ClearMyDebt',
        ogDescription: 'Berechne wann du schuldenfrei bist. Schneeball- und Lawinenmethode. Ohne Anmeldung. Sofortige Ergebnisse.',
      }}
      hreflangList={[
        { hreflang: 'de-de', href: 'https://clearmydebt.app/schuldenrechner/' },
        { hreflang: 'de-at', href: 'https://clearmydebt.app/schuldenrechner/' },
        { hreflang: 'de-ch', href: 'https://clearmydebt.app/schuldenrechner/' },
        { hreflang: 'x-default', href: 'https://clearmydebt.app/schuldenrechner/' },
      ]}
      h1="Schuldenrechner Kostenlos"
      intro="Gib deine Schulden ein und erfahre genau, wann du schuldenfrei bist — und wie viel du mit dem richtigen Schuldenplan sparst."
      sections={sections}
      faqItems={faqItems}
      faqHeading="Häufig gestellte Fragen"
      moreHeading="Weitere Rechner"
      moreLinks={[
        { href: '/de-de/credit-card-payoff-calculator/', label: 'Kreditkartenrechner' },
        { href: '/de-de/debt-snowball-calculator/', label: 'Schneeball-Methode' },
        { href: '/de-de/debt-avalanche-calculator/', label: 'Lawinen-Methode' },
        { href: '/de-de/debt-repayment-calculator/', label: 'Tilgungsrechner' },
      ]}
    />
  );
}
